/**
 * Pure decision logic for the app update gate — no native/network imports, so it
 * is trivially unit-testable. The IO (reading the installed version, fetching the
 * config, failing open) lives in appVersionApi.helper.ts.
 */

import { compareVersions } from '../../../helpers/versionCompare.helper';

export type UpdateStatus = 'ok' | 'soft' | 'hard';
export type AppPlatform = 'ios' | 'android' | 'web';

export interface PlatformVersionConfig {
  minVersion: string;
  recommendedVersion: string;
  message: string | null;
}

/**
 * Decide the gate state from the installed version and the server config.
 * Fails open to `'ok'` on any missing input.
 *
 *  - installed < minVersion          → 'hard' (blocking)
 *  - installed < recommendedVersion  → 'soft' (dismissible nudge)
 *  - otherwise                       → 'ok'
 */
export function deriveUpdateStatus(
  currentVersion: string | null,
  config: PlatformVersionConfig | null | undefined,
): UpdateStatus {
  if (!currentVersion) return 'ok';
  if (!config?.minVersion || !config?.recommendedVersion) return 'ok';
  if (compareVersions(currentVersion, config.minVersion) < 0) return 'hard';
  if (compareVersions(currentVersion, config.recommendedVersion) < 0) return 'soft';
  return 'ok';
}
